'use client';

import { useI18n } from '~/features/i18n';
import { CategoryGalleryBlock } from './CategoryGalleryBlock';
import { CategoryGalleryLoadingSkeleton } from './loader';
import { I18N_CONF } from '@funkey/locales/config';
import { createFetch, createSchema } from '@better-fetch/fetch';
import { z } from 'zod';
import { useMemo } from 'react';
import { LocalJSONStorage } from '~/utils/js';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '~/modules/trpc/client';

export const LocationBasedRegionBlock: React.FC = () => {
    const i18n = useI18n();
    const trpc = useTRPC();

    const geo = useGeoData();

    // prettier-ignore
    const region = useQuery(useMemo(() => {
        const geoCountryCodeIsValid = !!geo.data?.country_code && I18N_CONF.isValidCountry(geo.data?.country_code);

        return trpc.regions.searchClientRegion.queryOptions(
            // Input
            geoCountryCodeIsValid && geo.data
                ? {
                    countryCode: geo.data.country_code as I18N_CONF.Country,
                    language: i18n.language,

                    coords:
                        !!geo.data.latitude && !!geo.data.longitude
                            ? {
                                latitude: geo.data.latitude,
                                longitude: geo.data.longitude,
                            }
                            : undefined,

                    city: !!geo.data.postal ? { zipCode: geo.data.postal } : undefined,
                }
                : {
                    countryCode: i18n.country,
                    language: i18n.language,
                },

            // Options
            {
                enabled: !!geo.data,
            },
        );
    }, [geo.data, i18n.country]));

    if (geo.isLoading || region.isLoading)
        return <CategoryGalleryLoadingSkeleton className="container" />;
    if (region.error || !region.data) return null;

    return (
        <CategoryGalleryBlock
            key="region.gallery"
            blok={{
                _uid: 'location-based-region',
                type: 'region',
                typeId: region.data.id.toString(),
                title: i18n._('home.region.category_gallery.title.{region}', {
                    region: region.data.name,
                }),
                url: {
                    url: I18N_CONF.getTranslatedUrl('teambuildings', {
                        locale: i18n.locale,
                        slug: `?regions=${region.data.id}`,
                    }),
                },
                component: 'CategoryGalleryBlock',
            }}
            config={{
                filterType: 'regions',
                filterId: region.data.id,
                refetchOnMount: true,
            }}
        />
    );
};

function useGeoData() {
    const sessionStorage = useMemo(
        () => new LocalJSONStorage<
            z.infer<(typeof IP_API.SCHEMA.schema)['@get/json']['output']> | null
        >('geo.byIP', null, 'sessionStorage'),
        []); // prettier-ignore

    return useQuery({
        queryKey: ['geo', 'byIP'],
        async queryFn() {
            const cached = sessionStorage.getValue();
            if (cached) return cached;

            const locationData = await IP_API.fetch('@get/json', { throw: true });

            if (locationData) sessionStorage.setValue(locationData);

            return locationData;
        },
        staleTime: Infinity,
    });
}

namespace IP_API {
    export const SCHEMA = createSchema({
        /** Infers the IP based on the request origin and returns its geolocation */
        '@get/json': {
            output: z.object({
                ip: z.string().nullable().default(null),
                // network: '2a02:1808:1a0::/43',
                /** @example 'IPv6' */
                version: z.string(),
                /** @example 'Brussels' */
                city: z.string().nullable().catch(null),
                /** @example 'Brussels Capital' */
                region: z.string().nullable().catch(null),
                // region_code: 'BRU',
                // country: 'BE',
                // country_name: 'Belgium',
                /** @example 'BE' */
                country_code: z.string().nullable().catch(null),
                // country_code_iso3: 'BEL',
                // country_capital: 'Brussels',
                // country_tld: '.be',
                // continent_code: 'EU',
                // in_eu: true,
                /** @example '1030' */
                postal: z.string().nullable().catch(null),
                latitude: z.number().nullable().catch(null),
                longitude: z.number().nullable().catch(null),
                // timezone: 'Europe/Brussels',
                // utc_offset: '+0200',
                // country_calling_code: '+32',
                // currency: 'EUR',
                // currency_name: 'Euro',
                // languages: 'nl-BE,fr-BE,de-BE',
                // country_area: 30510,
                // country_population: 11422068,
                // asn: 'AS6848',
                // org: 'Telenet BV',
            }),
        },
    });

    export const fetch = createFetch({
        baseURL: 'https://ipapi.co',
        schema: SCHEMA,
        retry: 0,
    });
}
